/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import { AUTH_STATE_LIST } from '@/config/index';
import { replaceObjectKey } from '@/utils/transfer';
import http from './driver';
import { UserFull, UserMedal, ImUserInfo, ProfileOptionsData, GameOption, UserGameItem, FunCode, FunCodeStock, AdminItem, AdminProps, UserBasic, UserBlocker, UserAccess, UserKeyword, UserFollower, UserVipData, UserCouple, UserVerify, UserCoinTransferInfo, UserPointLog, UserAtListData, AdminPayment, UserFieldInfos, UserAccount, WebrtcOptions } from './types/user';

export const getUser = (strict = true, silent = false) => http.get<UserFull>('user/profile', {
  strict: strict ? 'Y' : 'N',
}, {
  ignoreAuth: !strict,
  errcodeExpected: silent ? AUTH_STATE_LIST : [],
});
export const getOnlineUser = (game, gender, limit, point) => http.get<UserBasic[]>('user/onlines', { game, gender, limit, point }, { silent: true });
export const getPeek = ({ game, id, xid, name, phone, full = 'N', silent = false }: {
  game?: string;
  id?: string|number;
  xid?: string;
  name?: string;
  phone?: string;
  full?: 'Y' | 'N';
  silent?: boolean;
}) => http.get<UserBasic>('user/peek', { id, xid, name, phone, full, game }, { silent });

export const getPhoneCode = (phone, captcha, scene, errcodeExpected) => {
  let hash = 0x1505; // 预定义素数
  for (let i = 0; i < phone.length; i += 1) {
    hash += hash << 5;
    hash += phone.charCodeAt(i); // 手机号第X位的 ASCII 值
    hash &= 0x7fffffff;
  }
  const csum = hash.toString(16); // 转为十六进制
  return http.get(`phone-code/${encodeURIComponent(phone)}`, { csum, captcha, scene }, { modal: true, errcodeExpected });
};
export const setPhone = ({ body, errcodeExpected }) => http.put('user/phone', body, { errcodeExpected });
export const login = (phone, code) => http.post('login', { phone, code }, { modal: true });
export const logout = () => http.delete('tokens/mine');

export const getProfileOptions = silent => http.get<ProfileOptionsData>('user/profile-options', {}, { silent });
export const setProfile = (body, opt) => http.put('user/profile', replaceObjectKey(body, { realName: 'real_name' }), opt);
export const checkRealName = name => http.post('user/check-name', { name });

export const getAccess = () => http.get<UserAccess>('user/access');
export const getVip = () => http.get<UserVipData>('user/vip-data');

export const getFollowers = () => http.get<UserFollower[]>('user/followers');
export const delFollowers = id => http.delete(`user/followers/${id}`);
export const setFollowers = id => http.put(`user/followers/${id}`);

export const getBlockers = () => http.get<UserBlocker[]>('user/blockers');
export const deleteBlocker = id => http.delete(`user/blockers/${id}`);
export const getBlocker = id => http.get(`user/blockers/${id}`);
export const setBlocker = (id, body) => http.put(`user/blockers/${id}`, body);

export const getKeyWords = () => http.get<UserKeyword[]>('user/keywords');
export const deleteKeyWord = word => http.delete(`user/keywords/${word}`);
export const setKeyWord = word => http.post('user/keywords', { word });

export const findUser = body => http.get('user/find', body, { modal: true });
export const matchUser = (body, silent) => http.get<UserFull[]>('user/matches', body, { modal: !silent, silent });

export const deleteAdmin = id => http.delete(`admins/${id}`);
export const getAdmins = () => http.get<AdminItem[]>('admins');
export const getAdmin = id => http.get<AdminProps>(`admins/${id}`);
export const setAdmin = (id, body) => http.put(`admins/${id}`, body);
export const delAdmin = id => http.delete(`admins/${id}`);

export const getCp = () => http.get<UserCouple>('user/cp');
export const delCp = opt => http.delete('user/cp', opt);
export const setCp = (name, opt) => http.post('user/cp', { name }, opt);

export const setPrisoner = (id, body) => http.put(`user/prisoners/${id}`, body);

export const getAtList = kw => http.get<UserAtListData>('user/atlist', { kw }, { silent: true });

export const getGames = id => http.get<UserGameItem[]>('user/games', { id });

export const getGameOptions = (id, scene) => http.get<GameOption>(`user/game-options/${id}`, { scene });
export const setGame = (id, body) => http.put(`user/games/${id}`, body);
export const getVerifiedInfo = id => http.get<UserVerify>(`user/verified-info/${id}`);
export const postVerifiedInfo = info => http.post('user/verified-info', info);
export const putVerifiedInfo = (id, state) => http.put(`user/verified-info/${id}`, { state });
export const getVerifiedInfoList = (filter, point) => http.get<UserVerify[]>(`user/verified-info/${filter}`, { point });

export const getImUserSig = silent => http.get('user/im-user-sig', {}, { silent });
export const getImUserInfo = (uids: string[], silent: boolean) => http.post<ImUserInfo[]>('user/im-user-info', uids, { silent });

export const setOrder = (type, body) => http.post(`${type}/orders`, replaceObjectKey(body, { tradeType: 'trade_type' }));
export const getOrder = (id, silent) => http.get(`pay/orders/${id}`, {}, { silent });
export const getCoinTransferInfo = () => http.get<UserCoinTransferInfo>('user/coin-transfer-info');
export const postCoinTransfer = amount => http.post('user/coin-transfer', { amount });
export const getCoinLog = (point, limit, kw) => http.get<UserPointLog[]>('user/coin-logs', { point, limit, kw });
export const getPointLogs = (kw, limit, point) => http.get<UserPointLog[]>('user/point-logs', { kw, limit, point });
export const getCodeSales = prefix => http.get('user/codes/sales', { prefix });
export const getCodeMine = () => http.get<FunCode[]>('user/codes/mine');
export const exchangeCode = (feature, opt) => http.post('user/codes/exchange', { feature }, opt);
export const useCode = (id, opt = {}) => http.put(`user/codes/${id}`, {}, opt);
export const getCode = id => http.get<FunCode>(`user/codes/${id}`);
export const exchangeUseCode = (feature, extra) => http.post('user/codes/exchange-use', { feature, extra });
// 超管功能
export const createCode = body => http.post('user/codes', body);
export const getCodeFeatures = () => http.get<Record<string, string>>('user/codes/features');
export const getCodeStocks = () => http.get<FunCodeStock[]>('user/codes/stocks');
export const setCodeStocks = (feature, lines) => http.post(`user/codes/stocks/${feature}`, { lines });
export const getCodes = () => http.get<FunCode[]>('user/codes');
export const getOrders = () => http.get<AdminPayment[]>('/admin/pay-orders');

// field=exp,charm,wealth
export const getFieldInfos = (field, uid) => http.get<UserFieldInfos>(`user/field-infos/${field}`, { uid });

// accounts
export const getAccounts = (errcodeExpected) => http.get<UserAccount[]>('user/accounts', {}, { errcodeExpected });
export const createAccount = (phone, code) => http.post('user/accounts', { phone, code });
export const setAccount = xid => http.put('user/accounts/current', { xid });

export interface MedalsData {
  data: UserMedal[];
  extra: {
    finishedCount: number;
    totalCount: number;
  };
}
export const getMedals = () => http.get<MedalsData>('user/medals');
export const putMedal = (id: number) => http.put('user/medal', { id });

export const getWebrtcOptions = () => http.get<WebrtcOptions>('tokens/webrtc/options');
export const createWebrtcToken = (body, opt) => http.post('tokens/webrtc', body, opt);
